<template>

<div style="display: 'flex'; padding: 0; align-items: 'start';">
    <!-- Button to trigger modal visibility -->
    <button type="button" class="btn btn-primary"  :disabled="disabled" :style="customStyle" @click="openModal" v-html="title">
    </button>

    <!-- Modal Structure -->
    <div
      v-if="showModal"
      class="modal fade show d-block"
      tabindex="-1"
      role="dialog"
      aria-labelledby="jsonModalLabel"
      style="background: rgba(0, 0, 0, 0.8);"
    >
      <div class="modal-dialog modal-lg responsive-modal" role="document">
        <div class="modal-content bg-dark text-light">
          <div class="modal-header border-bottom border-secondary">
            <div class="header-top-row">
              <div class="modal-title-container">
            <h5 class="modal-title" id="jsonModalLabel">
              {{ viewOnly ? 'JSON Viewer' : 'JSON Editor' }}
                  <span v-if="localEditMode && !viewOnly" class="edit-mode-badge ml-2">
                    <i class="fas fa-pencil-alt"></i> Edit Mode
                  </span>
                  <span v-if="!localEditMode && !viewOnly" class="read-mode-badge ml-2">
                <i class="fas fa-eye"></i> Read Only
              </span>
            </h5>
              </div>
            <div class="editor-toggle">
              <button 
                type="button" 
                class="btn btn-sm btn-outline-light mr-2" 
                :class="{ active: !rawTextMode }"
                  @click="switchToFieldView"
              >
                  Field View
              </button>
              <button 
                type="button" 
                class="btn btn-sm btn-outline-light" 
                :class="{ active: rawTextMode }"
                  @click="switchToRawText"
              >
                Raw Text
              </button>
              </div>
            </div>
            <div v-if="variantName || parameterName || audienceName" class="modal-subtitle-row">
              <div v-if="variantName" class="subtitle-line">Variant: {{ variantName }}</div>
              <div v-if="parameterName" class="subtitle-line">Parameter: {{ parameterName }}</div>
              <div v-if="audienceName" class="subtitle-line">
                <span>Audience: </span>
                <span 
                  v-if="audience && audience.conditions && audience.conditions.length > 0"
                  class="audience-name-clickable"
                  @click="openAudienceConditions"
                  title="Click to view audience conditions"
                >
                  {{ audienceName }}
                </span>
                <span v-else>{{ audienceName }}</span>
                <span 
                  v-if="audience && audience.conditions && audience.conditions.length > 0"
                  class="audience-hint-text"
                >
                  (click to see audience conditions)
                </span>
              </div>
            </div>
            
            <!-- Category and Priority Fields -->
            <div v-if="!useLegacySave && showCategoryPriority && audienceName !== 'Default' && !viewOnly" class="modal-metadata-row">
              <div class="metadata-field">
                <label class="metadata-label">Category:</label>
                <select 
                  v-model="localCategory" 
                  class="form-control form-control-sm metadata-input"
                  :disabled="!localEditMode"
                >
                  <option value="">-</option>
                  <option
                    v-for="category in categories"
                    :key="`category-${category}`"
                    :value="category"
                  >
                    {{ category }}
                  </option>
                </select>
              </div>
              <div class="metadata-field">
                <label class="metadata-label">Priority:</label>
                <input
                  v-model.number="localPriority"
                  type="number"
                  class="form-control form-control-sm metadata-input"
                  placeholder="Priority"
                  :disabled="!localEditMode"
                />
              </div>
            </div>
          </div>
          <div class="modal-body scrollable-body">
            <!-- Raw text editor for large JSON -->
            <div v-if="rawTextMode" class="raw-text-container">
              <!-- Validation Status Bar -->
              <div v-if="!viewOnly" class="validation-status-bar mb-3">
                <div v-if="validationStatus === 'validating'" class="validation-indicator validating">
                  <i class="fas fa-spinner fa-spin"></i>
                  <span>Analyzing content...</span>
                </div>
                <div v-else-if="validationStatus === 'valid'" class="validation-indicator valid">
                  <i class="fas fa-check-circle"></i>
                  <span>Valid JSON</span>
                  <small class="validation-details">{{ validationMessage }}</small>
                </div>
                <div v-else-if="validationStatus === 'string'" class="validation-indicator string">
                  <i class="fas fa-font"></i>
                  <span>String Content</span>
                  <small class="validation-details">{{ validationMessage }}</small>
                </div>
                <div v-else-if="validationStatus === 'invalid'" class="validation-indicator invalid">
                  <i class="fas fa-times-circle"></i>
                  <span>Invalid JSON</span>
                  <small class="validation-details">{{ validationMessage }}</small>
                </div>
                <div v-else class="validation-indicator neutral">
                  <i class="fas fa-info-circle"></i>
                  <span>Type or paste content to analyze</span>
                </div>
              </div>

              <!-- Format Controls -->
              <div class="format-controls mb-3">
                <div class="format-buttons">
                <button 
                  type="button" 
                  class="btn btn-sm btn-outline-info mr-2"
                    :disabled="validationStatus !== 'valid' || !localEditMode"
                  @click="beautifyJson"
                  title="Format JSON with proper indentation"
                >
                  <i class="fas fa-magic"></i> Beautify
                </button>
                <button 
                  type="button" 
                  class="btn btn-sm btn-outline-warning mr-2"
                    :disabled="validationStatus !== 'valid' || !localEditMode"
                  @click="minifyJson"
                  title="Compress JSON to single line"
                >
                  <i class="fas fa-compress-alt"></i> Minify
                </button>
                <button 
                  type="button" 
                    class="btn btn-sm btn-outline-secondary mr-2"
                  @click="copyToClipboard"
                  title="Copy JSON to clipboard"
                >
                  <i class="fas fa-copy"></i> Copy
                </button>
                  <button 
                    type="button" 
                    class="btn btn-sm btn-outline-success mr-2"
                    @click="collapseAll"
                    :disabled="collapsedRegions.length === foldableRegions.length || foldableRegions.length === 0"
                    title="Collapse all regions"
                  >
                    <i class="fas fa-compress"></i> Collapse All
                  </button>
                  <button 
                    type="button" 
                    class="btn btn-sm btn-outline-success"
                    @click="expandAll"
                    :disabled="collapsedRegions.length === 0"
                    title="Expand all regions"
                  >
                    <i class="fas fa-expand"></i> Expand All
                </button>
                </div>
                <div class="search-controls">
                  <div v-if="showSearch" class="search-bar-container">
                    <input 
                      type="text" 
                      v-model="searchQuery"
                      @input="performSearchDebounced"
                      class="search-input"
                      placeholder="Search..."
                    />
                    <span v-if="searchMatches.length > 0" class="match-counter">
                      {{ currentMatchIndex + 1 }} / {{ searchMatches.length }}
                    </span>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-light"
                      @click="previousMatch"
                      :disabled="searchMatches.length === 0"
                      title="Previous match"
                    >
                      <i class="fas fa-chevron-up"></i>
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-light"
                      @click="nextMatch"
                      :disabled="searchMatches.length === 0"
                      title="Next match"
                    >
                      <i class="fas fa-chevron-down"></i>
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-light"
                      @click="closeSearch"
                      title="Close search"
                    >
                      <i class="fas fa-times"></i>
                    </button>
                  </div>
                  <button 
                    v-else
                    type="button" 
                    class="btn btn-sm btn-outline-light"
                    @click="openSearch"
                    title="Search in JSON"
                  >
                    <i class="fas fa-search"></i> Search
                  </button>
                </div>
              </div>
              
              <!-- JSON Editor with Line Numbers and Fold Controls -->
              <div class="json-editor-wrapper">
                <div class="json-editor-scroll-container" @scroll="syncScroll" ref="scrollContainer">
                  <div class="json-editor-content-wrapper">
                    <!-- Line Numbers & Fold Gutter -->
                    <div class="json-gutter">
                      <div class="gutter-content" ref="gutterContent">
                        <div 
                          v-for="(line, displayIndex) in displayJsonLines" 
                          :key="`gutter-${displayIndex}`"
                          class="gutter-row"
                        >
                          <span class="line-number">{{ (displayLineMap[displayIndex] !== undefined ? displayLineMap[displayIndex] : displayIndex) + 1 }}</span>
                          <button
                            v-if="displayLineMap[displayIndex] !== undefined && getFoldableRegionAtLine(displayLineMap[displayIndex])"
                            @click="toggleFold(getFoldableRegionAtLine(displayLineMap[displayIndex]).id)"
                            class="fold-button"
                            :title="isLineCollapsed(displayLineMap[displayIndex]) ? 'Expand' : 'Collapse'"
                          >
                            {{ isLineCollapsed(displayLineMap[displayIndex]) ? '▶' : '▼' }}
                          </button>
                          <span v-else class="fold-spacer"></span>
                        </div>
                      </div>
                    </div>
                    
                    <!-- Editor Area (Overlay + Textarea) -->
                    <div class="json-editor-container">
                      <div 
                        ref="syntaxOverlay"
                        class="syntax-highlight-overlay" 
                        v-html="highlightedDisplayJson"
                        :class="{ 'readonly-textarea': !localEditMode }"
                      ></div>
              <textarea
                        v-model="displayJsonText"
                        @input="handleTextareaInput"
                        @keydown="handleKeydown"
                        ref="jsonTextarea"
                        class="form-control raw-json-textarea json-textarea-overlay"
                        :class="{ 'readonly-textarea': !localEditMode }"
                        :readonly="!localEditMode"
                        :placeholder="localEditMode ? 'Enter JSON here...' : 'JSON content (read-only)'"
                        spellcheck="false"
              ></textarea>
                    </div>
                  </div>
                </div>
              </div>
              <div class="text-muted mt-2">
                <small v-if="localEditMode">Raw text mode - ideal for large JSON objects. Changes are validated in real-time.</small>
                <small v-else>Raw text mode - viewing JSON content in read-only mode.</small>
              </div>
            </div>
            <!-- Field editor for structured editing -->
            <div v-else>
              <json-generator :fields="fields" :readOnly="!localEditMode" @update-json="updateJson" />
            </div>
          </div>
          <div class="modal-footer border-top border-secondary">
            <div v-if="!viewOnly" class="footer-validation-info">
              <span v-if="validationStatus === 'valid'" class="text-success">
                <i class="fas fa-check"></i> Ready to submit{{ useLegacySave ? '' : ' to database' }}
              </span>
              <span v-else-if="validationStatus === 'string'" class="text-info">
                <i class="fas fa-font"></i> String content ready
              </span>
              <span v-else-if="validationStatus === 'invalid'" class="text-danger">
                <i class="fas fa-exclamation-triangle"></i> Fix JSON errors before submitting{{ useLegacySave ? '' : ' (can save progress)' }}
              </span>
            </div>
            <div class="footer-buttons">
              <button
                v-if="!localEditMode && !viewOnly"
                type="button"
                class="btn btn-info"
                @click="toggleEditMode"
              >
                <i class="fas fa-pencil-alt"></i> Switch to Edit Mode
              </button>
              <button
                v-else-if="localEditMode && !viewOnly"
                type="button"
                class="btn btn-outline-secondary"
                @click="toggleEditMode"
              >
                <i class="fas fa-eye"></i> Switch to Read-Only
              </button>
              <button type="button" class="btn btn-secondary" @click="closeModal">Close</button>

              <!-- Legacy workflow: Single "Save Content Locally" button -->
              <button
                v-if="useLegacySave && !viewOnly"
                type="button"
                class="btn btn-primary"
                :disabled="(validationStatus === 'invalid' && rawTextMode) || !localEditMode"
                @click="submitJsonLegacy"
              >
                <i class="fas fa-save"></i> Save Content Locally
              </button>

              <!-- New workflow: "Save Progress" + "Submit to Database" -->
              <template v-if="!useLegacySave && !viewOnly">
                <button
                  type="button"
                  class="btn btn-success"
                  :disabled="!localEditMode"
                  @click="saveProgress"
                >
                  <i class="fas fa-save"></i> Save Progress Locally
                </button>
                <button
                  type="button"
                  class="btn btn-primary"
                  :disabled="(validationStatus === 'invalid' && rawTextMode) || !localEditMode || isSubmitting"
                  @click="submitToDatabase"
                >
                  <i class="fas fa-cloud-upload-alt"></i> Submit to Database
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Audience Conditions Modal -->
    <audience-conditions 
      ref="audienceConditionsModal"
      :audience="audience"
      :audienceName="audienceName"
    />
  </div>
</template>

<script>
import JsonGenerator from "./JsonGenerator";
import AudienceConditions from "../AudienceConditions";
import Validator from "../../util/validator";
import notify from "../../util/notification";

export default {
  name: "JsonModal",
  components: {
    JsonGenerator,
    AudienceConditions,
  },
  props: {
    valueObject: {
      type: String,
      default: '',
    },
    customStyle : 
    {
      type: Object,
      default: ()=> {return {marginTop: '0px'}}, 
    },
    title:{
      type: String,
      default: "JSON Editor"
    },
    disabled:{
      type: Boolean,
      default: false
    },
    directOpen: {
      type: Boolean,
      default: false
    },
    editMode: {
      type: Boolean,
      default: false
    },
    mode: {
      type: String,
      default: 'edit' // 'add' or 'edit'
    },
    useLegacySave: {
      type: Boolean,
      default: false // If true, uses old "Save Content" button and json-submitted event
    },
    parameterName: {
      type: String,
      default: ''
    },
    audienceName: {
      type: String,
      default: ''
    },
    variantName: {
      type: String,
      default: ''
    },
    audience: {
      type: Object,
      default: null
    },
    currentCategory: {
      type: String,
      default: ''
    },
    currentPriority: {
      type: [Number, String],
      default: ''
    },
    showCategoryPriority: {
      type: Boolean,
      default: true
    },
    viewOnly: {
      type: Boolean,
      default: false
    },
    categories: {
      type: Array,
      default: () => ['Internal', 'External', 'Other']
    }
  },
  data() {
    return {
      fields: [],
      showModal: false,
      cachedJsonString: '', // Cache the JSON string to avoid re-processing
      rawTextMode: true,
      rawJsonText: '',
      validationStatus: null, // 'valid', 'invalid', 'validating', 'string'
      validationMessage: '',
      validationTimer: null,
      contentType: 'unknown', // 'json', 'string', 'unknown'
      localEditMode: false, // Internal edit mode state
      localCategory: '', // Local state for category
      localPriority: '', // Local state for priority
      isSubmitting: false, // Prevent double-clicking submit
      searchQuery: '',
      searchMatches: [],
      currentMatchIndex: -1,
      showSearch: false,
      searchDebounceTimer: null,
      // Collapse/Expand state
      fullJsonText: '', // Complete JSON (source of truth)
      displayJsonText: '', // Currently visible JSON in editor
      foldableRegions: [], // All regions that can be folded
      collapsedRegions: [], // Currently collapsed regions
      collapsedContent: {}, // Stores original content for each collapsed region (key: regionId, value: content)
      showLineNumbers: true,
      displayLineMap: [] // Maps display line index to original line index
    };
  },
  computed: {
    highlightedJson() {
      if (!this.rawJsonText) return '';
      
      // Apply syntax highlighting first
      let highlighted = this.syntaxHighlight(this.rawJsonText);
      
      // Then add search match highlighting on top
      if (this.searchQuery && this.searchMatches.length > 0) {
        highlighted = this.addSearchHighlights(highlighted);
      }
      
      return highlighted;
    },
    
    displayJsonLines() {
      return this.displayJsonText.split('\n');
    },
    
    highlightedDisplayJson() {
      if (!this.displayJsonText) return '';
      
      // Apply syntax highlighting to display version
      let highlighted = this.syntaxHighlight(this.displayJsonText);
      
      // Add search highlighting if active
      if (this.searchQuery && this.searchMatches.length > 0) {
        highlighted = this.addSearchHighlights(highlighted);
      }
      
      return highlighted;
    },
  },
  created(){
    // Do nothing - completely passive until openModal is called
    // This prevents any processing during component creation
  },
  mounted() {
    // Handle directOpen after component is fully mounted
    if (this.directOpen) {
      this.openModal();
    }
    
    // Add keyboard shortcut listener
    window.addEventListener('keydown', this.handleKeyboardShortcut);
  },
  watch: {
    // Watches removed - using explicit event handlers instead
  },
  methods: {
    handleKeyboardShortcut(event) {
      if ((event.metaKey || event.ctrlKey) && event.key === 's') {
        event.preventDefault(); // Prevent browser's default save dialog
        // Only handle if modal is open and in edit mode
        if (!this.showModal || !this.localEditMode) {
          return;
        }
        this.saveProgress();
      }
    },
    
    openModal() {
      // Set up the modal content
      const currentJsonString = this.valueObject != "" ? this.valueObject : '{}';
      this.rawJsonText = currentJsonString;
      
      // Initialize collapse/expand state
      this.fullJsonText = currentJsonString;
      this.displayJsonText = currentJsonString;
      this.foldableRegions = [];
      this.collapsedRegions = [];
      // Initialize 1:1 line mapping
      const initialLines = currentJsonString.split('\n');
      this.displayLineMap = initialLines.map((_, index) => index);
      
      // Initialize local edit mode from prop
      this.localEditMode = this.editMode;
      if (this.viewOnly) {
        this.localEditMode = false;
      }
      
      // Initialize category and priority from props
      this.localCategory = this.currentCategory || '';
      this.localPriority = this.currentPriority || '';
      
      // Reset validation state
      this.validationStatus = null;
      this.validationMessage = '';
      
      // Always default to raw text mode
      this.rawTextMode = true;
      console.log('Modal opened, defaulting to raw text mode');
      
      // Auto-beautify if JSON is valid
      this.autoBeautifyIfValid(currentJsonString);
      
      // Parse foldable regions after beautification
      this.$nextTick(() => {
        this.parseFoldableRegions();
        this.adjustEditorHeight();
      });
      
      // Pre-process for field editor in background (if reasonable size) so it's ready if user switches
      if (currentJsonString.length <= 10000 && this.cachedJsonString !== currentJsonString) {
        console.log('Pre-processing JSON for field editor in background...');
        this.cachedJsonString = currentJsonString;
        this.processJsonNonBlocking(currentJsonString);
      }
      
      this.showModal = true;
    },
    
    adjustEditorHeight(immediate = false) {
      // Dynamically adjust editor height based on content
      const doAdjustment = () => {
        const wrapper = this.$el ? this.$el.querySelector('.json-editor-wrapper') : null;
        
        if (!wrapper) return;
        
        const lineCount = (this.displayJsonText || '').split('\n').length;
        const lineHeight = 21; // 14px font * 1.5 line-height = 21px
        const padding = 20; // Top and bottom padding
        const minHeight = 200;
        const maxHeight = window.innerHeight - 400; // Leave room for header/footer
        
        // Calculate ideal height based on content
        let idealHeight = (lineCount * lineHeight) + padding;
        
        // Clamp between min and max
        const newHeight = Math.max(minHeight, Math.min(idealHeight, maxHeight));
        
        // Apply the height
        wrapper.style.height = `${newHeight}px`;
      };
      
      // If immediate is true, apply synchronously for instant feedback during typing
      // Otherwise use $nextTick for DOM updates after programmatic changes
      if (immediate) {
        doAdjustment();
      } else {
        this.$nextTick(doAdjustment);
      }
    },

    processJsonNonBlocking(jsonString) {
      // Show loading state
      this.fields = [{
        key: '_loading',
        value: 'Processing JSON...',
        type: 'string',
        indentation: 0,
        isNotice: true
      }];

      // Use requestIdleCallback or setTimeout for non-blocking processing
      const processJson = () => {
        try {
          const result = this.convertJsonToFieldArray(jsonString);
          // Ensure result is an array before assigning
          if (Array.isArray(result)) {
            this.fields = result;
          } else {
            this.fields = [{
              key: '_processing_error',
              value: 'Failed to process JSON for field view.',
              type: 'string',
              indentation: 0,
              isNotice: true
            }];
          }
        } catch (error) {
          this.fields = [{
            key: 'error',
            value: `Failed to process JSON: ${error.message}`,
            type: 'string',
            indentation: 0,
            isNotice: true
          }];
        }
      };

      if (typeof requestIdleCallback !== 'undefined') {
        requestIdleCallback(processJson, { timeout: 1000 });
      } else {
        setTimeout(processJson, 0);
      }
    },
    closeModal() {
      this.showModal = false;
      this.isSubmitting = false;
      // Clear validation timer
      if (this.validationTimer) {
        clearTimeout(this.validationTimer);
        this.validationTimer = null;
      }
      // Clear search debounce timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
      // Clear collapse/expand timers
      if (this._reparseTimer) {
        clearTimeout(this._reparseTimer);
        this._reparseTimer = null;
      }
      if (this._foldButtonUpdateTimer) {
        clearTimeout(this._foldButtonUpdateTimer);
        this._foldButtonUpdateTimer = null;
      }
      // Clear collapsed content to prevent memory leaks
      this.collapsedContent = {};
      this.collapsedRegions = [];
      this.foldableRegions = [];
      
      this.$emit('modal-closed');
    },
    toggleEditMode() {
      if (this.viewOnly) return;
      this.localEditMode = !this.localEditMode;
      this.$emit('mode-changed', this.localEditMode);
      console.log('Edit mode toggled to:', this.localEditMode);
    },
    
    openAudienceConditions() {
      if (this.$refs.audienceConditionsModal) {
        this.$refs.audienceConditionsModal.openModal();
      }
    },
    
    handleTextareaInput(event) {
      // Only process in edit mode
      if (!this.localEditMode) {
        return;
      }
      
      // New simple approach: Check if user is editing a placeholder
      if (this.collapsedRegions.length > 0) {
        const textarea = this.$refs.jsonTextarea;
        if (!textarea) return;
        
        const cursorPos = textarea.selectionStart;
        
        // Step 1: Check if user is trying to edit a placeholder - if so, expand it
        // Check for placeholder patterns in the current display text
        const placeholderPattern = /\/\*\s*\d+\s+(lines|items)\s+collapsed\s*\*\//;
        
        // Find the line the cursor is on
        const textBeforeCursor = this.displayJsonText.substring(0, cursorPos);
        const currentLineStart = textBeforeCursor.lastIndexOf('\n') + 1;
        const currentLineEnd = this.displayJsonText.indexOf('\n', cursorPos);
        const currentLine = this.displayJsonText.substring(
          currentLineStart,
          currentLineEnd === -1 ? undefined : currentLineEnd
        );
        
        // Check if the placeholder itself has been corrupted (not just typing around it)
        let matchingRegion = null;
        let isPlaceholderCorrupted = false;
        
        for (const region of this.collapsedRegions) {
          const placeholder = this.generatePlaceholder(region);
          
          // Check if this line contains this placeholder
          if (currentLine.includes(placeholder)) {
            // Placeholder is intact - but check if trailing content (comma) was deleted
            matchingRegion = region;
            
            // Get expected trailing content from fullJsonText
            const fullLines = this.fullJsonText.split('\n');
            if (region.endLine < fullLines.length) {
              const fullLastLine = fullLines[region.endLine];
              const closingDelimiter = region.type === 'object' ? '}' : ']';
              const closingIndex = fullLastLine.lastIndexOf(closingDelimiter);
              const expectedTrailing = closingIndex !== -1 ? fullLastLine.substring(closingIndex + 1) : '';
              
              // Get actual trailing content from current line
              const placeholderEnd = currentLine.indexOf(placeholder) + placeholder.length;
              const actualTrailing = currentLine.substring(placeholderEnd);
              
              // If trailing content doesn't match (user deleted comma), expand
              if (expectedTrailing && !actualTrailing.includes(expectedTrailing.trim())) {
                isPlaceholderCorrupted = true;
                console.log('Trailing content deleted (comma removed) - expanding:', region.key);
              } else {
                isPlaceholderCorrupted = false;
                console.log('Placeholder intact - allowing edit:', region.key);
              }
            }
            break;
          } else if (region.key && currentLine.includes(`"${region.key}":`)) {
            // This line has the region's key but placeholder is missing/corrupted
            matchingRegion = region;
            isPlaceholderCorrupted = true;
            console.log('Placeholder corrupted (key found, placeholder missing):', region.key);
            break;
          } else if (!region.key && currentLine.includes('collapsed')) {
            // Array placeholder (no key) with corrupted placeholder
            matchingRegion = region;
            isPlaceholderCorrupted = true;
            console.log('Placeholder corrupted (array, placeholder broken)');
            break;
          }
        }
        
        if (matchingRegion && isPlaceholderCorrupted) {
          console.log('Placeholder corrupted - auto-expanding region:', matchingRegion.key);
          
          // Store the region info before removing
          const expandedRegionStartLine = matchingRegion.startLine;
          const expandedRegionEndLine = matchingRegion.endLine;
          
          // Remove this region from collapsed and delete its stored content
          this.collapsedRegions = this.collapsedRegions.filter(r => r.id !== matchingRegion.id);
          delete this.collapsedContent[matchingRegion.id];
          
          // Rebuild display
          this.updateDisplayJson();
          
          // Place cursor at the end of the closing brace/bracket of the expanded region
          this.$nextTick(() => {
            if (textarea) {
              // Find the position at the end of the expanded region
              const lines = this.displayJsonText.split('\n');
              let charCount = 0;
              
              // Count characters up to and including the end line of the expanded region
              for (let i = 0; i <= expandedRegionEndLine && i < lines.length; i++) {
                if (i < expandedRegionEndLine) {
                  charCount += lines[i].length + 1; // +1 for newline
                } else {
                  // On the last line, find the closing delimiter and position after it
                  const closingDelimiter = matchingRegion.type === 'object' ? '}' : ']';
                  const closingIndex = lines[i].indexOf(closingDelimiter);
                  if (closingIndex !== -1) {
                    charCount += closingIndex + 1; // Position right after the closing delimiter
                  } else {
                    charCount += lines[i].length; // Fallback to end of line
                  }
                }
              }
              
              textarea.setSelectionRange(charCount, charCount);
              textarea.focus();
            }
          });
          return;
        }
        
        // Step 2: Reconstruct fullJsonText from displayJsonText by replacing placeholders
        console.log('Reconstructing fullJsonText from displayJsonText');
        this.fullJsonText = this.reconstructFullJsonText();
        this.rawJsonText = this.fullJsonText;
        
        console.log('Reconstructed fullJsonText (first 200 chars):', this.fullJsonText.substring(0, 200));
        
        // Step 3: Update fold button positions IMMEDIATELY
        // Save cursor position before updating display (textarea already declared above)
        const savedCursorPos = textarea ? textarea.selectionStart : 0;
        
        // Update line numbers and rebuild display with correct placeholder positions
        this.updateFoldableRegionsLineNumbers();
        
        // Rebuild display to show placeholders at correct positions
        this.updateDisplayJson();
        
        // Restore cursor position
        this.$nextTick(() => {
          if (textarea) {
            textarea.setSelectionRange(savedCursorPos, savedCursorPos);
          }
        });
        
        // Step 4: Validate
        this.validateJsonDebounced();
        
        // Note: adjustEditorHeight is already called in updateDisplayJson()
        
        return;
      }
      
      // No collapsed regions - normal edit
      // Update fullJsonText with user's edit
      this.fullJsonText = this.displayJsonText;
      this.rawJsonText = this.displayJsonText;
      
      // Adjust height immediately for instant feedback (synchronous)
      this.adjustEditorHeight(true);
      
      // Validate the JSON
      this.validateJsonDebounced();
      
      // Reparse foldable regions after a delay (debounced)
      clearTimeout(this._reparseTimer);
      this._reparseTimer = setTimeout(() => {
        this.parseFoldableRegions();
      }, 1000);
    },
    
    handleKeydown(event) {
      // Only handle in edit mode
      if (!this.localEditMode) {
        return;
      }
      
      const textarea = event.target;
      
      // Handle Tab key
      if (event.key === 'Tab') {
        event.preventDefault();
        
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const value = textarea.value;
        
        if (event.shiftKey) {
          // Shift+Tab: Unindent (remove spaces from start of line)
          const lineStart = value.lastIndexOf('\n', start - 1) + 1;
          const lineEnd = value.indexOf('\n', start);
          const currentLine = value.substring(lineStart, lineEnd === -1 ? value.length : lineEnd);
          
          // Check if line starts with spaces
          const spaceMatch = currentLine.match(/^(\s+)/);
          if (spaceMatch) {
            const spaces = spaceMatch[1];
            // Remove up to 2 spaces (or however many are there)
            const spacesToRemove = Math.min(2, spaces.length);
            
            // Update the text
            const newValue = value.substring(0, lineStart) + 
                           value.substring(lineStart + spacesToRemove);
            
            this.displayJsonText = newValue;
            
            // Restore cursor position
            this.$nextTick(() => {
              textarea.setSelectionRange(
                Math.max(lineStart, start - spacesToRemove),
                Math.max(lineStart, end - spacesToRemove)
              );
            });
          }
        } else {
          // Tab: Insert 2 spaces (standard JSON indentation)
          const newValue = value.substring(0, start) + '  ' + value.substring(end);
          
          this.displayJsonText = newValue;
          
          // Restore cursor position after the inserted spaces
          this.$nextTick(() => {
            textarea.setSelectionRange(start + 2, start + 2);
          });
        }
      }
    },
    
    reconstructFullJsonText() {
      // Reconstruct complete JSON by replacing placeholders with stored content
      // Stored content does NOT include trailing commas - those are in displayJsonText
      let result = this.displayJsonText;
      
      // For each collapsed region, replace its placeholder with the stored content
      this.collapsedRegions.forEach(region => {
        const placeholder = this.generatePlaceholder(region);
        const storedContent = this.collapsedContent[region.id];
        
        if (storedContent && result.includes(placeholder)) {
          // Find the placeholder line in displayJsonText
          const lines = result.split('\n');
          const lineIndex = lines.findIndex(line => line.includes(placeholder));
          
          if (lineIndex !== -1) {
            const currentLine = lines[lineIndex];
            
            // Find what comes after the placeholder (comma, semicolon, etc)
            const placeholderEnd = currentLine.indexOf(placeholder) + placeholder.length;
            const trailingContent = currentLine.substring(placeholderEnd);
            
            // Append trailing content to the last line of stored content
            const storedLines = storedContent.split('\n');
            if (trailingContent) {
              storedLines[storedLines.length - 1] += trailingContent;
            }
            
            // Replace the placeholder line with stored content
            lines.splice(lineIndex, 1, ...storedLines);
            result = lines.join('\n');
          }
        }
      });
      
      return result;
    },
    
    updateFoldableRegionsLineNumbers() {
      // After reconstruction, line numbers may have changed
      // Reparse foldable regions to get updated line numbers
      const oldCollapsedKeys = this.collapsedRegions.map(r => ({ 
        key: r.key, 
        type: r.type, 
        id: r.id,
        indent: r.indent,
        parentId: r.parentId
      }));
      
      // Reparse to get new line numbers
      this.parseFoldableRegions();
      
      // Update collapsed regions to reference the new foldable regions with updated line numbers
      // IMPORTANT: We only update line numbers, NOT the stored content
      const newCollapsedRegions = [];
      
      oldCollapsedKeys.forEach(old => {
        // Match by key, type, indent, AND parentId to handle duplicate keys at different nesting levels
        // This ensures we match the exact same region even if multiple regions have the same key
        const matchingRegion = this.foldableRegions.find(r => 
          r.key === old.key && 
          r.type === old.type && 
          r.indent === old.indent && 
          r.parentId === old.parentId
        );
        
        if (matchingRegion) {
          newCollapsedRegions.push({
            id: matchingRegion.id,
            startLine: matchingRegion.startLine,
            endLine: matchingRegion.endLine,
            type: matchingRegion.type,
            key: matchingRegion.key,
            indent: matchingRegion.indent,
            parentId: matchingRegion.parentId
          });
          
          // DON'T update stored content here!
          // Stored content should remain stable until user explicitly expands/re-collapses
          // Only transfer the stored content to the new region ID if it changed
          if (old.id !== matchingRegion.id && this.collapsedContent[old.id]) {
            this.collapsedContent[matchingRegion.id] = this.collapsedContent[old.id];
            delete this.collapsedContent[old.id];
          }
        }
      });
      
      this.collapsedRegions = newCollapsedRegions;
    },
    
    // ===== COLLAPSE/EXPAND METHODS =====
    
    parseFoldableRegions() {
      const jsonText = this.fullJsonText;
      if (!jsonText) {
        this.foldableRegions = [];
        return;
      }
      
      const lines = jsonText.split('\n');
      const regions = [];
      const stack = [];
      
      lines.forEach((lineText, lineIndex) => {
        const trimmed = lineText.trim();
        const indent = lineText.search(/\S/);
        
        // Detect opening braces/brackets
        if (trimmed.endsWith('{') || trimmed.endsWith('[')) {
          const isObject = trimmed.endsWith('{');
          const keyMatch = lineText.match(/"([^"]+)"\s*:\s*[{[]/);
          
          stack.push({
            type: isObject ? 'object' : 'array',
            startLine: lineIndex,
            indent: indent >= 0 ? indent : 0,
            key: keyMatch ? keyMatch[1] : null,
            parentId: stack.length > 0 ? `region-${stack[stack.length - 1].startLine}` : null
          });
        }
        
        // Detect closing braces/brackets
        if (trimmed.startsWith('}') || trimmed.startsWith(']')) {
          if (stack.length > 0) {
            const opening = stack.pop();
            const endLine = lineIndex;
            
            // Only create region if it has content (more than 1 line)
            if (endLine - opening.startLine > 1) {
              const regionId = `region-${opening.startLine}-${endLine}`;
              
              regions.push({
                id: regionId,
                startLine: opening.startLine,
                endLine: endLine,
                type: opening.type,
                key: opening.key,
                indent: opening.indent,
                parentId: opening.parentId
              });
            }
          }
        }
      });
      
      this.foldableRegions = regions;
      console.log('Found foldable regions:', regions.length);
    },
    
    generatePlaceholder(region) {
      const lines = this.fullJsonText.split('\n');
      const lineCount = region.endLine - region.startLine - 1;
      const indent = ' '.repeat(region.indent);
      
      let prefix = '';
      if (region.key) {
        prefix = `"${region.key}": `;
      }
      
      if (region.type === 'object') {
        return `${indent}${prefix}{ /* ${lineCount} lines collapsed */ }`;
      } else {
        return `${indent}${prefix}[ /* ${lineCount} items collapsed */ ]`;
      }
    },
    
    toggleFold(regionId) {
      const region = this.foldableRegions.find(r => r.id === regionId);
      if (!region) return;
      
      const isCurrentlyCollapsed = this.collapsedRegions.some(r => r.id === regionId);
      
      if (isCurrentlyCollapsed) {
        // Expand
        this.expandRegion(regionId);
      } else {
        // Collapse
        this.collapseRegion(region);
      }
      
      this.updateDisplayJson();
    },
    
    collapseRegion(region) {
      // Store the original content WITHOUT trailing commas
      // Trailing commas will be managed in displayJsonText by the user
      const lines = this.fullJsonText.split('\n');
      const regionLines = [];
      for (let i = region.startLine; i <= region.endLine; i++) {
        regionLines.push(lines[i]);
      }
      
      // Remove trailing content from last line (comma, semicolon, etc)
      if (regionLines.length > 0) {
        const lastLine = regionLines[regionLines.length - 1];
        const closingDelimiter = region.type === 'object' ? '}' : ']';
        const closingIndex = lastLine.lastIndexOf(closingDelimiter);
        if (closingIndex !== -1) {
          regionLines[regionLines.length - 1] = lastLine.substring(0, closingIndex + 1);
        }
      }
      
      const originalContent = regionLines.join('\n');
      
      // Store it in collapsedContent map (WITHOUT trailing comma)
      this.collapsedContent[region.id] = originalContent;
      
      // Add to collapsed regions
      this.collapsedRegions.push({
        id: region.id,
        startLine: region.startLine,
        endLine: region.endLine,
        type: region.type,
        key: region.key,
        indent: region.indent,
        parentId: region.parentId
      });
    },
    
    expandRegion(regionId) {
      const index = this.collapsedRegions.findIndex(r => r.id === regionId);
      if (index > -1) {
        this.collapsedRegions.splice(index, 1);
        // Also delete the stored content
        delete this.collapsedContent[regionId];
      }
    },
    
    updateDisplayJson() {
      if (this.collapsedRegions.length === 0) {
        // No collapsed regions - display full JSON
        this.displayJsonText = this.fullJsonText;
        this.rawJsonText = this.fullJsonText; // Keep in sync
        
        // Build 1:1 mapping
        const lines = this.fullJsonText.split('\n');
        this.displayLineMap = lines.map((_, index) => index);
        return;
      }
      
      const lines = this.fullJsonText.split('\n');
      const result = [];
      const lineMap = [];
      
      // Sort collapsed regions by start line
      const sortedCollapsed = [...this.collapsedRegions].sort((a, b) => 
        a.startLine - b.startLine
      );
      
      // Build set of hidden lines
      const hiddenLines = new Set();
      sortedCollapsed.forEach(region => {
        // Mark lines between start and end as hidden (not including start line)
        for (let i = region.startLine + 1; i <= region.endLine; i++) {
          hiddenLines.add(i);
        }
      });
      
      // Build display JSON and line mapping
      lines.forEach((line, index) => {
        if (hiddenLines.has(index)) {
          return; // Skip hidden lines
        }
        
        // Check if this line starts a collapsed region
        const collapsedRegion = sortedCollapsed.find(r => r.startLine === index);
        if (collapsedRegion) {
          // Generate placeholder and include trailing content from the end line
          let placeholderLine = this.generatePlaceholder(collapsedRegion);
          
          // Get trailing content from the last line of the collapsed region (commas, etc)
          const lastLine = lines[collapsedRegion.endLine];
          const closingDelimiter = collapsedRegion.type === 'object' ? '}' : ']';
          const closingIndex = lastLine.lastIndexOf(closingDelimiter);
          
          if (closingIndex !== -1 && closingIndex < lastLine.length - 1) {
            const trailingContent = lastLine.substring(closingIndex + 1);
            placeholderLine += trailingContent;
          }
          
          result.push(placeholderLine);
        } else {
          result.push(line);
        }
        
        // Map display line to original line
        lineMap.push(index);
      });
      
      this.displayJsonText = result.join('\n');
      // DON'T sync rawJsonText with displayJsonText when there are placeholders
      // rawJsonText should always reflect the actual JSON for validation
      this.rawJsonText = this.fullJsonText;
      this.displayLineMap = lineMap;
      
      // Adjust editor height after updating display
      this.adjustEditorHeight();
    },
    
    expandAll() {
      this.collapsedRegions = [];
      this.collapsedContent = {}; // Clear all stored content
      this.updateDisplayJson();
      this.adjustEditorHeight();
    },
    
    collapseAll() {
      // Collapse ALL foldable regions and store their content
      const lines = this.fullJsonText.split('\n');
      
      this.collapsedRegions = this.foldableRegions.map(r => {
        // Store content for this region WITHOUT trailing commas
        const regionLines = [];
        for (let i = r.startLine; i <= r.endLine; i++) {
          regionLines.push(lines[i]);
        }
        
        // Remove trailing content from last line
        if (regionLines.length > 0) {
          const lastLine = regionLines[regionLines.length - 1];
          const closingDelimiter = r.type === 'object' ? '}' : ']';
          const closingIndex = lastLine.lastIndexOf(closingDelimiter);
          if (closingIndex !== -1) {
            regionLines[regionLines.length - 1] = lastLine.substring(0, closingIndex + 1);
          }
        }
        
        this.collapsedContent[r.id] = regionLines.join('\n');
        
        return {
          id: r.id,
          startLine: r.startLine,
          endLine: r.endLine,
          type: r.type,
          key: r.key,
          indent: r.indent,
          parentId: r.parentId
        };
      });
      this.updateDisplayJson();
      this.adjustEditorHeight();
    },
    
    getFoldableRegionAtLine(lineIndex) {
      return this.foldableRegions.find(r => r.startLine === lineIndex);
    },
    
    isLineCollapsed(lineIndex) {
      const region = this.getFoldableRegionAtLine(lineIndex);
      if (!region) return false;
      return this.collapsedRegions.some(r => r.id === region.id);
    },
    
    // ===== END COLLAPSE/EXPAND METHODS =====
    
    switchToRawText() {
      this.rawTextMode = true;
      this.$nextTick(() => {
        this.adjustEditorHeight();
      });
    },
    
    switchToFieldView() {
      // Ensure fields is always an array
      if (!Array.isArray(this.fields)) {
        this.fields = [];
      }
      
      // Validate JSON first if not already validated
      if (!this.validationStatus) {
          this.validateJson(this.rawJsonText);
      }
      
      // Check if content is valid JSON
      if (this.validationStatus === 'string') {
        // String content - can't display in field view
        this.fields = [{
          key: '_string_content_notice',
          value: 'Field view is only available for JSON objects and arrays. Your content is plain text. Please use Raw Text mode to edit this content.',
          type: 'string',
          indentation: 0,
          isNotice: true
        }];
        this.rawTextMode = false;
            return;
          }
      
      if (this.validationStatus === 'invalid') {
        // Invalid JSON - can't display in field view
        this.fields = [{
          key: '_invalid_json_notice',
          value: `Field view is only available for valid JSON. Your content has JSON errors: ${this.validationMessage}. Please fix the errors in Raw Text mode first.`,
          type: 'string',
          indentation: 0,
          isNotice: true
        }];
        this.rawTextMode = false;
        return;
      }
      
      // Check if JSON is too large for field view
      const jsonLength = this.rawJsonText.length;
      
      if (jsonLength > 50000) {
        // Very large JSON - don't even try to process
        this.fields = [{
          key: '_too_large_notice',
          value: `JSON is too large for field view (${(jsonLength / 1024).toFixed(1)}KB). Field view is optimized for JSONs under 50KB. Please use Raw Text mode to edit this content.`,
          type: 'string',
          indentation: 0,
          isNotice: true
        }];
        this.rawTextMode = false;
        return;
      }
      
      // Check if we need to process the JSON for field view
      if (this.cachedJsonString !== this.rawJsonText) {
        // JSON hasn't been processed yet
        if (jsonLength > 10000) {
          // Large JSON - show notice and process
          this.fields = [{
            key: '_processing_notice',
            value: `Processing JSON for field view (${(jsonLength / 1024).toFixed(1)}KB). This may take a moment...`,
            type: 'string',
            indentation: 0,
            isNotice: true
          }];
          this.rawTextMode = false;
          
          // Process after a short delay to show the message
          setTimeout(() => {
            this.cachedJsonString = this.rawJsonText;
            this.processJsonNonBlocking(this.rawJsonText);
          }, 100);
        } else {
          // Small JSON - process immediately
          this.cachedJsonString = this.rawJsonText;
          try {
            const result = this.convertJsonToFieldArray(this.rawJsonText);
            // Ensure result is an array
            if (Array.isArray(result)) {
              this.fields = result;
            } else {
              this.fields = [{
                key: '_processing_error',
                value: 'Failed to process JSON for field view.',
                type: 'string',
                indentation: 0,
                isNotice: true
              }];
            }
          } catch (error) {
            this.fields = [{
              key: '_processing_error',
              value: `Failed to process JSON: ${error.message}`,
              type: 'string',
              indentation: 0,
              isNotice: true
            }];
          }
          this.rawTextMode = false;
        }
      } else {
        // Already processed, just switch mode
        // Ensure fields is still an array
        if (!Array.isArray(this.fields)) {
          this.fields = [];
        }
        this.rawTextMode = false;
      }
    },
    syntaxHighlight(json) {
      // Escape HTML to prevent XSS
      json = json.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      
      // Highlight different JSON elements with spans
      return json.replace(/("(\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(true|false|null|undefined)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?|[{}[\],])/g, (match) => {
        let cls = 'json-number';
        if (/^"/.test(match)) {
          if (/:$/.test(match)) {
            cls = 'json-key';
          } else {
            cls = 'json-string';
          }
        } else if (/true|false/.test(match)) {
          cls = 'json-boolean';
        } else if (/null|undefined/.test(match)) {
          cls = 'json-null';
        } else if (/[{}[\],]/.test(match)) {
          cls = 'json-punctuation';
        }
        return '<span class="' + cls + '">' + match + '</span>';
      });
    },
    addSearchHighlights(htmlString) {
      if (!this.searchQuery) return htmlString;
      
      // Escape special regex characters in search query
      const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedQuery = escapeRegex(this.searchQuery);
      
      // Create regex to match search query (case insensitive)
      // Use a negative lookahead to avoid matching inside HTML tags
      const regex = new RegExp(`(?![^<]*>)(${escapedQuery})`, 'gi');
      
      let matchCount = 0;
      return htmlString.replace(regex, (match) => {
        const isCurrentMatch = matchCount === this.currentMatchIndex;
        matchCount++;
        const className = isCurrentMatch ? 'search-match search-match-current' : 'search-match';
        return `<mark class="${className}">${match}</mark>`;
      });
    },
    syncScroll(event) {
      // The scroll container now handles all scrolling
      // We don't need to sync anything - CSS positioning handles it
      // This method is kept for future enhancements if needed
    },
    openSearch() {
      this.showSearch = true;
      this.$nextTick(() => {
        const searchInput = this.$el.querySelector('.search-input');
        if (searchInput) {
          searchInput.focus();
        }
      });
    },
    closeSearch() {
      this.showSearch = false;
      this.searchQuery = '';
      this.searchMatches = [];
      this.currentMatchIndex = -1;
      // Clear debounce timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
        this.searchDebounceTimer = null;
      }
    },
    performSearchDebounced() {
      // Clear existing timer
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer);
      }
      
      // Debounce the search to avoid interfering with typing (200ms for good responsiveness)
      this.searchDebounceTimer = setTimeout(() => {
        this.performSearch();
      }, 200);
    },
    performSearch() {
      if (!this.searchQuery) {
        this.searchMatches = [];
        this.currentMatchIndex = -1;
        return;
      }

      // Use fullJsonText to search (includes collapsed content)
      const text = this.fullJsonText || this.rawJsonText;
      const query = this.searchQuery;
      const matches = [];
      let index = 0;

      while (index < text.length) {
        const foundIndex = text.toLowerCase().indexOf(query.toLowerCase(), index);
        if (foundIndex === -1) break;
        matches.push({
          start: foundIndex,
          end: foundIndex + query.length
        });
        index = foundIndex + 1;
      }

      this.searchMatches = matches;
      
      if (matches.length > 0) {
        // Check if any matches are inside collapsed regions and auto-expand them
        if (this.collapsedRegions.length > 0) {
          this.expandRegionsContainingMatches(matches, text);
        }
        
        this.currentMatchIndex = 0;
        // Don't focus textarea when searching from input - just scroll to it
        this.scrollToMatch(0, false);
      } else {
        this.currentMatchIndex = -1;
      }
    },
    
    expandRegionsContainingMatches(matches, fullText) {
      // Convert character positions to line numbers
      const lines = fullText.split('\n');
      const matchLines = new Set();
      
      matches.forEach(match => {
        const textBeforeMatch = fullText.substring(0, match.start);
        const lineNumber = (textBeforeMatch.match(/\n/g) || []).length;
        matchLines.add(lineNumber);
      });
      
      // Find which collapsed regions contain these line numbers
      const regionsToExpand = [];
      
      this.collapsedRegions.forEach(region => {
        // Check if any match line falls within this collapsed region
        for (const matchLine of matchLines) {
          if (matchLine >= region.startLine && matchLine <= region.endLine) {
            regionsToExpand.push(region.id);
            break;
          }
        }
      });
      
      // Auto-expand regions containing matches
      if (regionsToExpand.length > 0) {
        console.log(`Auto-expanding ${regionsToExpand.length} regions containing search matches`);
        
        regionsToExpand.forEach(regionId => {
          this.expandRegion(regionId);
        });
        
        // Rebuild display after expanding
        this.updateDisplayJson();
        
        // Update search matches to use the new displayJsonText
        this.$nextTick(() => {
          // Recalculate matches based on displayJsonText since regions are now expanded
          const displayText = this.displayJsonText;
          const query = this.searchQuery;
          const newMatches = [];
          let index = 0;

          while (index < displayText.length) {
            const foundIndex = displayText.toLowerCase().indexOf(query.toLowerCase(), index);
            if (foundIndex === -1) break;
            newMatches.push({
              start: foundIndex,
              end: foundIndex + query.length
            });
            index = foundIndex + 1;
          }

          this.searchMatches = newMatches;
        });
      }
    },
    nextMatch() {
      if (this.searchMatches.length === 0) return;
      this.currentMatchIndex = (this.currentMatchIndex + 1) % this.searchMatches.length;
      this.scrollToMatch(this.currentMatchIndex, true);
    },
    previousMatch() {
      if (this.searchMatches.length === 0) return;
      this.currentMatchIndex = (this.currentMatchIndex - 1 + this.searchMatches.length) % this.searchMatches.length;
      this.scrollToMatch(this.currentMatchIndex, true);
    },
    scrollToMatch(index, shouldFocus = true) {
      const match = this.searchMatches[index];
      if (!match) return;

      const textarea = this.$refs.jsonTextarea;
      const scrollContainer = this.$refs.scrollContainer;
      if (!textarea || !scrollContainer) return;

      // Calculate line number to scroll to - use displayJsonText since that's what's shown
      const textBeforeMatch = this.displayJsonText.substring(0, match.start);
      const lineNumber = (textBeforeMatch.match(/\n/g) || []).length;
      const lineHeight = 21; // 14px font-size * 1.5 line-height = 21px
      const targetScrollTop = Math.max(0, lineNumber * lineHeight - scrollContainer.clientHeight / 2);
      
      if (shouldFocus) {
        // When navigating with buttons, focus and select the text
        // Use preventScroll to avoid browser auto-scrolling the page
        textarea.focus({ preventScroll: true });
        textarea.setSelectionRange(match.start, match.end);
      }
      
      // Scroll the container
      scrollContainer.scrollTop = targetScrollTop;
    },
    saveProgress() {
      // Allow saving even invalid JSON (work-in-progress)
      let stringJson;
      
      if (this.rawTextMode) {
        // Save whatever the user has typed, even if invalid
        stringJson = this.fullJsonText || this.rawJsonText || '';
      } else {
        // For field editor, generate JSON
        try {
          stringJson = JSON.stringify(this.generateJson());
        } catch (error) {
          notify('Error generating JSON from fields', 'error');
          return;
        }
      }
      
      // Emit with metadata
      this.$emit("json-progress-saved", {
        value: stringJson,
        category: this.localCategory,
        priority: this.localPriority
      });
      
      // Show notification with appropriate message
      if (this.validationStatus === 'invalid') {
        notify('Progress saved locally (JSON has errors) - Changes not submitted to database', 'warning');
      } else {
        notify('Progress saved locally - Changes not submitted to database', 'info');
      }
    },
    
    submitToDatabase() {
      if (this.isSubmitting) return;
      
      const stringJson = this.getValidatedJson();
      if (!stringJson) return;
      
      this.isSubmitting = true;
      
      // Emit with metadata
      this.$emit("json-submitted-to-db", {
        value: stringJson,
        category: this.localCategory,
        priority: this.localPriority
      });
      
      // Reset after a short delay (modal will close anyway)
      setTimeout(() => {
        this.isSubmitting = false;
      }, 1000);
      
      this.closeModal();
    },
    
    submitJsonLegacy() {
      // Legacy save workflow for ExperimentForm and TemplateEdit
      const stringJson = this.getValidatedJson();
      if (!stringJson) return;
      
      // Emit legacy event
      this.$emit("json-submitted", stringJson);
      
      // Notify user that changes are saved locally
      notify(`Variant ${this.variantName ? this.variantName : ''} JSON Saved Locally (Changes not submitted to Database)`, 'info');
      
      this.closeModal();
    },
    
    getValidatedJson() {
      let stringJson;
      
      if (this.rawTextMode) {
        // Use fullJsonText if available (in case there are collapsed regions)
        // Otherwise fall back to rawJsonText
        const jsonToSubmit = this.fullJsonText || this.rawJsonText;
        
        // Ensure we have a validation status
        if (!this.validationStatus) {
          this.validateJson(jsonToSubmit);
        }
        
        // Check validation status
        if (this.validationStatus === 'invalid') {
          // Invalid JSON - cannot submit
          alert(`Cannot submit invalid JSON: ${this.validationMessage}`);
          return null;
        } else if (this.validationStatus === 'valid') {
          // Valid JSON - parse and re-stringify for consistency
          try {
            const parsed = JSON.parse(jsonToSubmit);
            stringJson = JSON.stringify(parsed);
          } catch (error) {
            alert(`Invalid JSON: ${error.message}`);
            return null;
          }
        } else if (this.validationStatus === 'string') {
          // Plain string content - submit as-is (trimmed)
          stringJson = jsonToSubmit.trim();
        } else {
          // Unknown status - should not happen but handle it
          alert('Unable to determine content type. Please try again.');
          return null;
        }
      } else {
        // Use field editor - assume valid since it's generated from valid structure
        try {
          stringJson = JSON.stringify(this.generateJson());
          // Validate the generated JSON just to be safe
          JSON.parse(stringJson);
        } catch (error) {
          alert(`Error generating JSON from fields: ${error.message}`);
          return null;
        }
      }
      
      return stringJson;
    },
    
    updateJson(newFields) {
      this.fields = newFields;
    
    },
    generateJson() {
      return this.buildJsonObject(this.fields);
    },
    buildJsonObject(fields) {
      const jsonObject = {};
      fields.forEach(field => {
        // Skip notice fields
        if (field.isNotice) {
          return;
        }
        
        if (field.type === 'object') {
          jsonObject[field.key] = this.buildJsonObject(field.value);
        } else if (field.type === 'array') {
          jsonObject[field.key] = field.value || [];
        } else {
          jsonObject[field.key] = field.value;
        }
      });
      return jsonObject;
    },
    convertJsonToFieldArray(jsonString, indentation = 0) {
      try {
        console.log("Processing JSON string of length:", jsonString.length);
        
        // Use Web Workers for large JSON processing if available
        if (jsonString.length > 10000 && typeof Worker !== 'undefined') {
          return this.processLargeJsonAsync(jsonString, indentation);
        }
        
        const jsonObject = JSON.parse(jsonString);
        
        // Use a more efficient streaming approach for large objects
        return this.processJsonIteratively(jsonObject, indentation);
        
      } catch (error) {
        console.log('Value is not in JSON format:', error.message);
        return [{
          key: 'error',
          value: `JSON parsing failed: ${error.message}`,
          type: 'string',
          indentation: 0
        }];
      }
    },

    processLargeJsonAsync(jsonString, indentation) {
      // For very large JSON, use a simplified field structure
      console.log('Using async processing for large JSON');
      
      try {
        const jsonObject = JSON.parse(jsonString);
        const keys = Object.keys(jsonObject);
        
        // Create a simplified view for large objects
        return keys.slice(0, 50).map((key, index) => {
          const value = jsonObject[key];
          const field = {
            key: key,
            type: this.getValueType(value),
            indentation: indentation,
            simplified: true
          };
          
          // For objects and arrays, we need to provide proper structure
          if (field.type === 'object') {
            field.value = []; // Empty array for nested JsonGenerator
            field.tooDeep = true; // Mark as too deep to show notice
          } else if (field.type === 'array') {
            field.arrayType = value.length > 0 ? typeof value[0] : 'string';
            field.value = value.slice(0, 5); // Show first 5 items only
            if (value.length > 5) {
              field.truncated = true;
              field.originalLength = value.length;
            }
          } else {
            field.value = this.getSimplifiedValue(value);
          }
          
          return field;
        }).concat([{
          key: '_large_json_notice',
          value: `Large JSON with ${keys.length} total fields. Showing first 50 fields. Use raw text editing for full access.`,
          type: 'string',
          indentation: 0,
          isNotice: true
        }]);
        
      } catch (error) {
        return [{
          key: 'error',
          value: `Large JSON processing failed: ${error.message}`,
          type: 'string',
          indentation: 0
        }];
      }
    },

    processJsonIteratively(jsonObject, indentation) {
      // Use requestIdleCallback for non-blocking processing
      const result = [];
      const startTime = performance.now();
      const maxProcessingTime = 50; // 50ms max per chunk
      
      const processChunk = (obj, currentIndentation, parentKey = '') => {
        const entries = Object.entries(obj);
        let processed = 0;
        
        for (const [key, value] of entries) {
          // Break if we've been processing too long
          if (performance.now() - startTime > maxProcessingTime) {
            result.push({
              key: '_processing_paused',
              value: `Processing paused after ${processed} fields to maintain UI responsiveness. ${entries.length - processed} fields remaining.`,
              type: 'string',
              indentation: currentIndentation,
              isNotice: true
            });
            break;
          }
          
          const fieldKey = parentKey ? `${parentKey}.${key}` : key;
          const field = {
            key: fieldKey,
            value: '',
            type: '',
            indentation: currentIndentation,
          };
          
          if (Array.isArray(value)) {
            field.type = "array";
            field.arrayType = value.length > 0 ? typeof value[0] : "string";
            field.value = value.slice(0, 10); // Reduced from 20 to 10
            if (value.length > 10) {
              field.truncated = true;
              field.originalLength = value.length;
            }
          } else if (typeof value === 'object' && value !== null) {
            field.type = 'object';
            // Process nested object recursively with increased indentation
            field.value = this.processNestedObject(value, currentIndentation + 20);
          } else {
            field.type = this.getValueType(value);
            field.value = this.getSimplifiedValue(value);
          }
          
          result.push(field);
          processed++;
          
          // Limit total fields for performance
          if (result.length >= 100) {
            result.push({
              key: '_field_limit_reached',
              value: `Field limit reached (100 fields). Use raw text editing for complete JSON access.`,
              type: 'string',
              indentation: currentIndentation,
              isNotice: true
            });
            break;
          }
        }
      };
      
      processChunk(jsonObject, indentation);
      return result;
    },

    getValueType(value) {
      if (typeof value === 'string') return 'string';
      if (typeof value === 'number') return 'number';
      if (typeof value === 'boolean') return 'boolean';
      if (Array.isArray(value)) return 'array';
      if (typeof value === 'object') return 'object';
      return 'string';
    },

    getSimplifiedValue(value) {
      if (typeof value === 'string') {
        return value.length > 200 ? value.substring(0, 200) + '...' : value;
      }
      if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) {
          return `Array(${value.length})`;
        }
        return `Object(${Object.keys(value).length} keys)`;
      }
      return value;
    },

    // Process nested objects recursively for field editor
    processNestedObject(obj, indentation) {
      const nestedFields = [];
      const entries = Object.entries(obj);
      
      // Limit nested object processing to prevent performance issues
      const maxNestedFields = 20;
      const entriesToProcess = entries.slice(0, maxNestedFields);
      
      for (const [key, value] of entriesToProcess) {
        const field = {
          key: key,
          value: '',
          type: '',
          indentation: indentation,
        };
        
        if (Array.isArray(value)) {
          field.type = "array";
          field.arrayType = value.length > 0 ? typeof value[0] : "string";
          field.value = value.slice(0, 5); // Limit array items in nested objects
          if (value.length > 5) {
            field.truncated = true;
            field.originalLength = value.length;
          }
        } else if (typeof value === 'object' && value !== null) {
          field.type = 'object';
          // Limit nesting depth to prevent infinite recursion
          if (indentation < 60) { // Max 3 levels deep
            field.value = this.processNestedObject(value, indentation + 20);
          } else {
            field.value = [];
            field.tooDeep = true;
            field.originalValue = value;
          }
        } else {
          field.type = this.getValueType(value);
          field.value = value;
        }
        
        nestedFields.push(field);
      }
      
      // Add notice if there are more fields
      if (entries.length > maxNestedFields) {
        nestedFields.push({
          key: '_nested_limit_notice',
          value: `Showing first ${maxNestedFields} of ${entries.length} nested fields. Switch to raw text mode for complete access.`,
          type: 'string',
          indentation: indentation,
          isNotice: true
        });
      }
      
      return nestedFields;
    },

    // JSON Validation Methods
    validateJsonDebounced() {
      // Clear existing timer
      if (this.validationTimer) {
        clearTimeout(this.validationTimer);
      }
      
      // Set validation status to validating
      this.validationStatus = 'validating';
      this.validationMessage = '';
      
      // Always validate fullJsonText (actual JSON) not displayJsonText (may have placeholders)
      const jsonToValidate = this.fullJsonText || this.rawJsonText;
      
      // Debounce validation for better performance
      const debounceTime = jsonToValidate.length > 50000 ? 1000 : 500;
      
      this.validationTimer = setTimeout(() => {
        this.validateJson(jsonToValidate);
      }, debounceTime);
    },

    hasUnmatchedBrackets(str) {
      const stack = [];
      const pairs = {
        '{': '}',
        '[': ']'
      };
      const closingBrackets = {'}': '{', ']': '['};
      
      for (let char of str) {
        if (pairs[char]) {
          // Opening bracket
          stack.push(char);
        } else if (closingBrackets[char]) {
          // Closing bracket
          if (stack.length === 0 || stack[stack.length - 1] !== closingBrackets[char]) {
            // Unmatched closing bracket
            return true;
          }
          stack.pop();
        }
      }
      
      // If stack is not empty, there are unclosed opening brackets
      return stack.length > 0;
    },

    validateJson(jsonString) {
      if (!jsonString || jsonString.trim() === '') {
        this.validationStatus = null;
        this.validationMessage = '';
        this.contentType = 'unknown';
        return;
      }

      const trimmedString = jsonString.trim();
      
      // Check if it has any brackets/braces at all
      const hasBrackets = /[{}\[\]]/.test(trimmedString);
      
      // Check if it looks like JSON (starts with { or [)
      const looksLikeJson = trimmedString.startsWith('{') || trimmedString.startsWith('[');
      
      // Check for unmatched brackets
      const hasUnmatched = hasBrackets && this.hasUnmatchedBrackets(trimmedString);
      
      try {
        const startTime = performance.now();
        const parsed = JSON.parse(jsonString);
        const parseTime = performance.now() - startTime;
        
        // Successful parsing - it's valid JSON
        this.validationStatus = 'valid';
        this.contentType = 'json';
        
        // Provide helpful information about the JSON
        const jsonInfo = this.getJsonInfo(parsed, jsonString.length, parseTime);
        this.validationMessage = jsonInfo;
        
      } catch (error) {
        // Failed to parse
        if (looksLikeJson || hasUnmatched) {
          // It looks like JSON or has unmatched brackets - show as invalid JSON
          this.validationStatus = 'invalid';
          this.contentType = 'json';
          this.validationMessage = hasUnmatched 
            ? 'Unmatched brackets or braces detected'
            : this.getDetailedErrorMessage(error, jsonString);
        } else {
          // It doesn't look like JSON and brackets are matched - treat as string content
          this.validationStatus = 'string';
          this.contentType = 'string';
          this.validationMessage = this.getStringInfo(jsonString);
        }
      }
    },

    getJsonInfo(parsed, stringLength, parseTime) {
      const info = [];
      
      // Size information
      if (stringLength > 1000) {
        info.push(`${(stringLength / 1024).toFixed(1)}KB`);
      } else {
        info.push(`${stringLength} chars`);
      }
      
      // Structure information
      if (Array.isArray(parsed)) {
        info.push(`Array with ${parsed.length} items`);
      } else if (typeof parsed === 'object' && parsed !== null) {
        const keyCount = Object.keys(parsed).length;
        info.push(`Object with ${keyCount} key${keyCount !== 1 ? 's' : ''}`);
      } else {
        info.push(`${typeof parsed} value`);
      }
      
      // Performance information for large JSON
      if (parseTime > 10) {
        info.push(`parsed in ${parseTime.toFixed(1)}ms`);
      }
      
      return info.join(' • ');
    },

    getStringInfo(stringContent) {
      const info = [];
      
      // Length information
      if (stringContent.length > 1000) {
        info.push(`${(stringContent.length / 1024).toFixed(1)}KB`);
      } else {
        info.push(`${stringContent.length} characters`);
      }
      
      // Content type hints
      if (stringContent.includes('\n')) {
        const lineCount = stringContent.split('\n').length;
        info.push(`${lineCount} lines`);
      }
      
      // Check for common patterns
      if (stringContent.match(/^\d+$/)) {
        info.push('numeric string');
      } else if (stringContent.match(/^(true|false)$/i)) {
        info.push('boolean string');
      } else if (stringContent.includes('http')) {
        info.push('contains URL');
      }
      
      return info.join(' • ');
    },

    getDetailedErrorMessage(error, jsonString) {
      let message = error.message;
      
      // Try to provide more context for common JSON errors
      if (message.includes('Unexpected token')) {
        const match = message.match(/position (\d+)/);
        if (match) {
          const position = parseInt(match[1]);
          const lineInfo = this.getLineAndColumn(jsonString, position);
          message += ` at line ${lineInfo.line}, column ${lineInfo.column}`;
          
          // Show context around the error
          const context = this.getErrorContext(jsonString, position);
          if (context) {
            message += `\nNear: "${context}"`;
          }
        }
      }
      
      return message;
    },

    getLineAndColumn(jsonString, position) {
      const lines = jsonString.substring(0, position).split('\n');
      return {
        line: lines.length,
        column: lines[lines.length - 1].length + 1
      };
    },

    getErrorContext(jsonString, position) {
      const start = Math.max(0, position - 20);
      const end = Math.min(jsonString.length, position + 20);
      return jsonString.substring(start, end).replace(/\n/g, '\\n');
    },

    // JSON Formatting Methods
    beautifyJson() {
      if (this.validationStatus !== 'valid') {
        return;
      }

      try {
        const parsed = JSON.parse(this.fullJsonText || this.rawJsonText);
        const beautified = JSON.stringify(parsed, null, 2); // 2-space indentation
        
        // Update all relevant properties
        this.rawJsonText = beautified;
        this.fullJsonText = beautified;
        this.displayJsonText = beautified;
        
        // Clear collapsed regions and reparse
        this.collapsedRegions = [];
        this.$nextTick(() => {
          this.parseFoldableRegions();
          this.adjustEditorHeight();
        });
        
        // Update validation info after beautification
        this.validateJson(beautified);
        
        // Show success feedback
        this.showFormatFeedback('Beautified successfully!', 'success');
      } catch (error) {
        this.showFormatFeedback('Error beautifying JSON: ' + error.message, 'error');
      }
    },

    minifyJson() {
      if (this.validationStatus !== 'valid') {
        return;
      }

      try {
        const parsed = JSON.parse(this.fullJsonText || this.rawJsonText);
        const minified = JSON.stringify(parsed); // No spacing
        
        // Update all relevant properties
        this.rawJsonText = minified;
        this.fullJsonText = minified;
        this.displayJsonText = minified;
        
        // Clear collapsed regions (minified JSON has no foldable regions)
        this.collapsedRegions = [];
        this.foldableRegions = [];
        
        // Update validation info after minification
        this.validateJson(minified);
        
        // Adjust height for minified content
        this.$nextTick(() => {
          this.adjustEditorHeight();
        });
        
        // Show success feedback
        this.showFormatFeedback('Minified successfully!', 'success');
      } catch (error) {
        this.showFormatFeedback('Error minifying JSON: ' + error.message, 'error');
      }
    },

    async copyToClipboard() {
      try {
        if (navigator.clipboard && window.isSecureContext) {
          // Use modern clipboard API
          await navigator.clipboard.writeText(this.rawJsonText);
        } else {
          // Fallback for older browsers
          const textArea = document.createElement('textarea');
          textArea.value = this.rawJsonText;
          textArea.style.position = 'fixed';
          textArea.style.left = '-999999px';
          textArea.style.top = '-999999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          document.execCommand('copy');
          textArea.remove();
        }
        
        this.showFormatFeedback('Copied to clipboard!', 'success');
      } catch (error) {
        this.showFormatFeedback('Failed to copy to clipboard', 'error');
      }
    },

    showFormatFeedback(message, type) {
      // Temporarily show feedback in validation message
      const originalStatus = this.validationStatus;
      const originalMessage = this.validationMessage;
      
      this.validationStatus = type === 'success' ? 'valid' : 'invalid';
      this.validationMessage = message;
      
      // Restore original status after 2 seconds
      setTimeout(() => {
        this.validationStatus = originalStatus;
        this.validationMessage = originalMessage;
      }, 2000);
    },

    // Auto-beautify JSON when modal opens if valid
    autoBeautifyIfValid(jsonString) {
      if (!jsonString || jsonString.trim() === '' || jsonString === '{}') {
        // For empty or default JSON, just validate
        this.validateJson(jsonString);
        return;
      }

      try {
        // Test if JSON is valid
        const parsed = JSON.parse(jsonString);
        
        // Check if it's already beautified (has proper indentation)
        const beautified = JSON.stringify(parsed, null, 2);
        const isAlreadyBeautified = jsonString === beautified;
        
        if (!isAlreadyBeautified) {
          // Auto-beautify and update all relevant properties
          this.rawJsonText = beautified;
          this.fullJsonText = beautified;
          this.displayJsonText = beautified;
          console.log('Auto-beautified JSON on modal open');
        } else {
          // Already beautified - just ensure all properties are in sync
          this.fullJsonText = jsonString;
          this.displayJsonText = jsonString;
        }
        
        // Build initial 1:1 line mapping
        const lines = this.rawJsonText.split('\n');
        this.displayLineMap = lines.map((_, index) => index);
        
        // Validate the JSON (beautified or original)
        this.validateJson(this.rawJsonText);
        
      } catch (error) {
        // If invalid, just validate as-is to show error
        this.validateJson(jsonString);
      }
    },
  },

  beforeDestroy() {
    // Clean up validation timer
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    // Clean up search debounce timer
    if (this.searchDebounceTimer) {
      clearTimeout(this.searchDebounceTimer);
    }
    // Clean up collapse/expand timers
    if (this._reparseTimer) {
      clearTimeout(this._reparseTimer);
    }
    if (this._foldButtonUpdateTimer) {
      clearTimeout(this._foldButtonUpdateTimer);
    }
    // Remove keyboard shortcut listener
    window.removeEventListener('keydown', this.handleKeyboardShortcut);
  },
};
</script>

<style lang="scss" scoped>
@import "./styles/modal_elements.scss";
</style>
<style scoped>

.modal-dialog {
  max-width: 70%; /* Make the modal wider for better JSON editing */
}

/* Responsive modal sizing for different screen sizes */
.responsive-modal {
  max-width: 90%;
  width: 90%;
  margin: 1.75rem auto 2rem auto; /* Extra bottom margin for padding */
}

/* Larger screens */
@media (min-width: 1400px) {
  .responsive-modal {
    max-width: 85%;
    width: 85%;
  }
}

@media (min-width: 1800px) {
  .responsive-modal {
    max-width: 80%;
    width: 80%;
  }
}

/* Medium screens */
@media (max-width: 1199px) {
  .responsive-modal {
    max-width: 95%;
    width: 95%;
  }
}

/* Small screens */
@media (max-width: 768px) {
  .responsive-modal {
    max-width: 98%;
    width: 98%;
    margin: 0.5rem auto;
  }
}

.scrollable-body {
  max-height: calc(100vh - 250px); /* Dynamic height based on viewport, leaving room for header/footer */
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1.25rem 1rem 1rem 1rem; /* More top padding to separate from metadata */
}

/* Adjust padding for small screens */
@media (max-width: 768px) {
  .scrollable-body {
    max-height: calc(100vh - 200px);
    padding: 0.75rem;
  }
}

.modal-footer{
  padding-top: 1rem;
  padding-bottom: 1rem;
}

/* Modal content improvements */
.modal-content {
  max-height: calc(100vh - 3rem); /* Ensure modal doesn't overflow viewport */
  display: flex;
  flex-direction: column;
}

.modal-body {
  flex: 1;
  min-height: 0; /* Allow flexbox to shrink */
}

@media (max-width: 768px) {
  .modal-content {
    max-height: calc(100vh - 1rem);
  }
}

/* Modal Header Layout */
.modal-header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
}

.header-top-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.modal-title-container {
  flex: 0 1 auto;
  min-width: 0;
}

.modal-title {
  font-size: 1.15rem;
  margin-bottom: 0;
}

.modal-subtitle-row {
  width: 100%;
  padding-top: 0.4rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 14px;
  font-weight: 600;
  color: #cbd5e0;
}

.editor-toggle {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 0.25rem;
  margin-right: 0.5rem;
}

.editor-toggle .btn {
  position: relative;
  right: auto;
  top: auto;
  font-size: 0.875rem;
  padding: 0.35rem 0.75rem;
}

.editor-toggle .btn.active {
  background-color: #007bff;
  border-color: #007bff;
  color: white;
}

.raw-text-container {
  height: 100%;
}

.raw-json-textarea {
  background-color: #2d3748;
  color: #e2e8f0;
  border: 1px solid #4a5568;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
  resize: none; /* Disable manual resize for auto-sizing */
  min-height: 200px; /* Start smaller for small content */
  padding: 0.375rem 0.75rem;
  white-space: pre;
  overflow-wrap: normal;
  word-break: normal;
}

.raw-json-textarea:focus {
  border-color: #63b3ed;
  box-shadow: 0 0 0 0.2rem rgba(99, 179, 237, 0.25);
}

.raw-json-textarea::placeholder {
  color: #a0aec0;
}

/* Validation Status Styles */
.validation-status-bar {
  border-radius: 4px;
  padding: 8px 12px;
  border: 1px solid transparent;
}

.validation-indicator {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 500;
}

.validation-indicator i {
  margin-right: 8px;
  font-size: 16px;
}

.validation-indicator.validating {
  color: #3182ce;
  background-color: rgba(49, 130, 206, 0.1);
  border-color: rgba(49, 130, 206, 0.2);
}

.validation-indicator.valid {
  color: #38a169;
  background-color: rgba(56, 161, 105, 0.1);
  border-color: rgba(56, 161, 105, 0.2);
}

.validation-indicator.string {
  color: #3182ce;
  background-color: rgba(49, 130, 206, 0.1);
  border-color: rgba(49, 130, 206, 0.2);
}

.validation-indicator.invalid {
  color: #e53e3e;
  background-color: rgba(229, 62, 62, 0.1);
  border-color: rgba(229, 62, 62, 0.2);
}

.validation-indicator.neutral {
  color: #718096;
  background-color: rgba(113, 128, 150, 0.1);
  border-color: rgba(113, 128, 150, 0.2);
}

.validation-details {
  margin-left: auto;
  font-size: 12px;
  font-weight: normal;
  opacity: 0.8;
}

/* Footer Validation Info */
.modal-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-validation-info {
  flex: 1;
  font-size: 14px;
}

.footer-buttons {
  display: flex;
  gap: 10px;
  align-items: center;
}

.footer-buttons .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.footer-buttons .btn-info {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.footer-buttons .btn-info:hover {
  background-color: #138496;
  border-color: #117a8b;
}

.footer-buttons .btn-outline-secondary:hover {
  background-color: #6c757d;
  color: white;
}

/* Format Controls Styles */
.format-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: rgba(113, 128, 150, 0.05);
  border-radius: 4px;
  border: 1px solid rgba(113, 128, 150, 0.1);
}

.format-buttons {
  display: flex;
  align-items: center;
}

.format-controls .btn {
  font-size: 12px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.format-controls .btn i {
  font-size: 11px;
}

.format-controls .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Search Controls */
.search-controls {
  display: flex;
  align-items: center;
}

.search-bar-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-input {
  background-color: #2d3748;
  color: #e2e8f0;
  border: 1px solid #4a5568;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  width: 150px;
}

.search-input:focus {
  outline: none;
  border-color: #63b3ed;
}

.match-counter {
  color: #cbd5e0;
  font-size: 12px;
  white-space: nowrap;
}

.format-controls .btn-outline-info:not(:disabled):hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.format-controls .btn-outline-warning:not(:disabled):hover {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #212529;
}

.format-controls .btn-outline-secondary:not(:disabled):hover {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

/* Read-only mode styles */
.read-mode-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  background-color: rgba(255, 193, 7, 0.2);
  color: #ffc107;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid rgba(255, 193, 7, 0.3);
}

.read-mode-badge i {
  margin-right: 3px;
  font-size: 9px;
}

/* Edit mode styles */
.edit-mode-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 6px;
  background-color: rgba(66, 153, 225, 0.2);
  color: #4299e1;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
  border: 1px solid rgba(66, 153, 225, 0.3);
}

.edit-mode-badge i {
  margin-right: 3px;
  font-size: 9px;
}

/* Subtitle line styles */
.subtitle-line {
  margin: 3px 0;
  line-height: 1.4;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.audience-name-clickable {
  color: #63b3ed;
  text-decoration: underline;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.2s ease;
  padding: 2px 4px;
  border-radius: 3px;
}

.audience-name-clickable:hover {
  color: #4299e1;
  background-color: rgba(99, 179, 237, 0.15);
}

.audience-hint-text {
  color: #a0aec0;
  font-size: 12px;
  font-style: italic;
  margin-left: 4px;
}


.readonly-textarea {
  background-color: #1a202c !important;
  border-color: #4a5568 !important;
  cursor: default !important;
}

.readonly-textarea:focus {
  border-color: #4a5568 !important;
  box-shadow: none !important;
}

/* Disabled editor toggle buttons */
.editor-toggle .btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* JSON Editor Container with Syntax Highlighting */
.json-editor-wrapper {
  border-radius: 6px;
  border: 1px solid #3d5875;
  background: #1a2332;
  min-height: 200px; /* Start small for small content */
  max-height: calc(100vh - 400px); /* Grow up to viewport height minus header/footer/padding */
  height: auto; /* Auto-size based on content */
  position: relative;
  overflow: hidden;
}

.json-editor-scroll-container {
  width: 100%;
  height: 100%;
  overflow: auto;
}

.json-editor-content-wrapper {
  display: inline-flex;
  min-width: 100%;
  min-height: 100%;
}

.json-gutter {
  flex-shrink: 0;
  width: 70px;
  background: #0f1419;
  user-select: none;
  border-right: 1px solid #3d5875;
  position: sticky;
  left: 0;
  z-index: 10;
}

.gutter-content {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
  color: #6c8091;
  padding: 0.375rem 0;
  margin: 0;
  box-sizing: border-box;
}

.gutter-row {
  display: flex;
  align-items: center;
  height: 21px; /* 14px * 1.5 = 21px */
  line-height: 21px;
  padding: 0 4px;
  gap: 2px;
  box-sizing: border-box;
}

.line-number {
  min-width: 32px;
  text-align: right;
  padding-right: 4px;
  color: #4a5f7f;
  font-size: 12px;
}

.fold-button {
  background: transparent;
  border: none;
  color: #6c8091;
  cursor: pointer;
  padding: 0;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  border-radius: 2px;
  transition: all 0.2s;
  pointer-events: auto;
}

.fold-button:hover {
  background: #2a3f5f;
  color: #e2e8f0;
}

.fold-button:active {
  background: #3d5875;
}

.fold-spacer {
  width: 16px;
  height: 16px;
  display: inline-block;
}

.json-editor-container {
  position: relative;
  flex: 1;
  min-width: 0;
}

.syntax-highlight-overlay {
  padding: 0.375rem 0.75rem;
  margin: 0;
  background-color: #2d3748;
  color: #e2e8f0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre;
  pointer-events: none;
  box-sizing: border-box;
  min-height: 100%;
}

.json-textarea-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  min-height: 100%;
  z-index: 2;
  background: transparent !important;
  color: transparent !important;
  caret-color: #e2e8f0;
  -webkit-text-fill-color: transparent;
  padding: 0.375rem 0.75rem;
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
  white-space: pre !important;
  overflow-wrap: normal !important;
  word-break: normal !important;
  border: none !important;
  box-sizing: border-box !important;
  overflow: visible !important;
  resize: none;
  outline: none !important;
}

.json-textarea-overlay::selection {
  background-color: rgba(66, 153, 225, 0.5);
  color: transparent;
}

.json-textarea-overlay::-moz-selection {
  background-color: rgba(66, 153, 225, 0.5);
  color: transparent;
}

/* Search Match Highlighting */
.syntax-highlight-overlay >>> mark.search-match {
  background-color: rgba(255, 193, 7, 0.3);
  color: inherit;
  padding: 0;
  border-radius: 2px;
}

.syntax-highlight-overlay >>> mark.search-match-current {
  background-color: rgba(255, 152, 0, 0.6);
  color: inherit;
  padding: 0;
  border-radius: 2px;
  outline: 1px solid rgba(255, 152, 0, 0.8);
}

/* Syntax Highlighting Colors */
.syntax-highlight-overlay >>> .json-punctuation {
  color: #5e81ac !important; /* Dark blue for brackets/braces */
  font-weight: bold;
}

.syntax-highlight-overlay >>> .json-key {
  color: #88c0d0 !important; /* Lighter dark blue for keys */
  font-weight: normal;
}

.syntax-highlight-overlay >>> .json-string {
  color: #a3be8c !important; /* Greenish for string values */
}

.syntax-highlight-overlay >>> .json-number {
  color: #d08770 !important; /* Orange for numbers */
}

.syntax-highlight-overlay >>> .json-boolean {
  color: #b48ead !important; /* Pinkish for booleans */
}

.syntax-highlight-overlay >>> .json-null {
  color: #bf616a !important; /* Red for null/undefined */
}

/* Category and Priority Metadata Row */
.modal-metadata-row {
  width: 100%;
  padding-top: 0.6rem;
  padding-bottom: 0.75rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 1.5rem;
  align-items: center;
}

.metadata-field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.metadata-label {
  font-size: 14px;
  font-weight: 600;
  color: #cbd5e0;
  margin: 0;
  white-space: nowrap;
}

.metadata-input {
  max-width: 200px;
  background-color: #2d3748;
  color: #e2e8f0;
  border: 1px solid #4a5568;
  font-size: 13px;
}

.metadata-input:focus {
  border-color: #63b3ed;
  box-shadow: 0 0 0 0.2rem rgba(99, 179, 237, 0.25);
  background-color: #2d3748;
  color: #e2e8f0;
}

.metadata-input:disabled {
  background-color: #1a202c;
  border-color: #4a5568;
  cursor: not-allowed;
  opacity: 0.6;
}

</style>
